const CLASSES = {
    open: 'is-open',
    active: 'is-active',
    backdrop: 'mega-menu-backdrop',
};

const SELECTORS = {
    nav: '[data-desktop-nav]',
    header: '.site-header',
    trigger: '[data-mega-trigger]',
    panel: '[data-mega-panel]',
    close: '[data-mega-close]',
    megaTab: '[data-mega-tab]',
    tabPanel: '[role="tabpanel"]',
    menuItems: '[role="menuitem"]',
    backdrop: '.mega-menu-backdrop',
};

/**
 * Get numeric value from dataset
 */
const datasetNumber = (el, key, fallback = 0) => {
    const value = el?.dataset?.[key];
    const parsed = Number.parseInt(value, 10);

    return Number.isNaN(parsed) ? fallback : parsed;
};

/**
 * Announce message to screen readers
 */
const announceToScreenReader = (message) => {
    const liveRegion = document.getElementById('nav-announcements');
    if (liveRegion) {
        liveRegion.textContent = '';
        setTimeout(() => {
            liveRegion.textContent = message;
        }, 100);
    }
};

/**
 * Lock body scroll
 */
const lockScroll = () => {
    if (window.lenisInstance) {
        window.lenisInstance.stop();
    } else {
        // Fallback for when Lenis isn't available
        document.documentElement.style.overflow = 'hidden';
    }
};

/**
 * Unlock body scroll
 */
const unlockScroll = () => {
    if (window.lenisInstance) {
        window.lenisInstance.start();
    } else {
        document.documentElement.style.overflow = '';
    }
};

/**
 * Create backdrop element
 */
const createBackdrop = () => {
    // Check if backdrop already exists
    let backdrop = document.querySelector(SELECTORS.backdrop);

    if (backdrop) {
        // Reuse existing backdrop
        backdrop.classList.add(CLASSES.active);

        return backdrop;
    }

    backdrop = document.createElement('div');
    backdrop.className = CLASSES.backdrop;
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.appendChild(backdrop);

    // Trigger reflow for transition
    backdrop.offsetHeight;
    backdrop.classList.add(CLASSES.active);

    return backdrop;
};

/**
 * Remove backdrop element
 */
const removeBackdrop = () => {
    const backdrop = document.querySelector(SELECTORS.backdrop);
    if (backdrop) {
        backdrop.classList.remove(CLASSES.active);
        setTimeout(() => {
            backdrop.remove();
        }, 300);
    }
};

/**
 * Set active tab
 */
const setActiveTab = (container, index) => {
    const tabs = container.querySelectorAll(SELECTORS.megaTab);
    const panels = container.querySelectorAll(SELECTORS.tabPanel);

    tabs.forEach((tab) => {
        const tabIndex = datasetNumber(tab, 'megaTab');
        const isActive = tabIndex === index;

        tab.classList.toggle(CLASSES.active, isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel) => {
        const panelIndex = datasetNumber(panel, 'megaPanel');
        const isActive = panelIndex === index;

        panel.classList.toggle(CLASSES.active, isActive);

        if (isActive) {
            panel.removeAttribute('hidden');
            const links = panel.querySelectorAll('a, button');
            links.forEach((link) => link.setAttribute('tabindex', '0'));
        } else {
            panel.setAttribute('hidden', 'hidden');
            const links = panel.querySelectorAll('a, button');
            links.forEach((link) => link.setAttribute('tabindex', '-1'));
        }
    });
};

/**
 * Handle tab keyboard navigation
 */
const handleTabKeyboard = (event, container) => {
    const tabs = Array.from(container.querySelectorAll(SELECTORS.megaTab));
    const currentTab = event.target;
    const currentIndex = tabs.indexOf(currentTab);

    let nextIndex = currentIndex;

    switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
            event.preventDefault();
            nextIndex = (currentIndex + 1) % tabs.length;
            break;

        case 'ArrowLeft':
        case 'ArrowUp':
            event.preventDefault();
            nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
            break;

        case 'Home':
            event.preventDefault();
            nextIndex = 0;
            break;

        case 'End':
            event.preventDefault();
            nextIndex = tabs.length - 1;
            break;

        default:
            return;
    }

    tabs[nextIndex].focus();
    setActiveTab(container, datasetNumber(tabs[nextIndex], 'megaTab'));
};

/**
 * Initialize Desktop Mega Menu
 */
export const SiteHeaderNav = () => {
    const nav = document.querySelector(SELECTORS.nav);
    const header = document.querySelector(SELECTORS.header);

    if (!nav || !header) {
        console.warn('SiteHeaderNav: Required elements not found');

        return;
    }

    const triggers = Array.from(nav.querySelectorAll(SELECTORS.trigger));
    let openPanel = null;
    let openTrigger = null;

    /**
     * Set panel position
     */
    const setPanelPosition = (trigger, panel) => {
        if (!panel || !trigger) return;

        const headerBottom = header.getBoundingClientRect().bottom;
        panel.style.top = `${headerBottom}px`;

        const triggerRect = trigger.getBoundingClientRect();
        const notchLeft = triggerRect.left + triggerRect.width / 2;
        panel.style.setProperty('--mega-notch-left', `${notchLeft}px`);
        panel.style.setProperty('--mega-notch-width', `${triggerRect.width}px`);
    };

    /**
     * Close panel
     */
    const closePanel = (removeBackdropElement = true) => {
        if (!openPanel || !openTrigger) return;

        openTrigger.setAttribute('aria-expanded', 'false');
        openPanel.setAttribute('hidden', 'hidden');

        const links = openPanel.querySelectorAll('a, button');
        links.forEach((link) => link.setAttribute('tabindex', '-1'));

        const menuItem = openTrigger.closest('li');
        menuItem?.classList.remove(CLASSES.open);

        openTrigger.focus();

        const triggerText = openTrigger.textContent.trim();
        announceToScreenReader(`${triggerText} menu closed`);

        openPanel = null;
        openTrigger = null;

        if (removeBackdropElement) {
            removeBackdrop();
            unlockScroll();
        }
    };

    /**
     * Open panel
     */
    const openPanelMenu = (trigger) => {
        // Close any open panel first (but keep backdrop)
        if (openPanel) {
            closePanel(false);
        }

        const panelId = trigger.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);

        if (!panel) return;

        trigger.setAttribute('aria-expanded', 'true');
        panel.removeAttribute('hidden');

        setPanelPosition(trigger, panel);

        const menuItem = trigger.closest('li');
        menuItem?.classList.add(CLASSES.open);

        const tabs = panel.querySelectorAll(SELECTORS.megaTab);
        const links = panel.querySelectorAll('a, button');

        if (tabs.length > 0) {
            setActiveTab(panel, 0);
        } else {
            links.forEach((link) => link.setAttribute('tabindex', '0'));
        }

        openPanel = panel;
        openTrigger = trigger;

        // Create or reuse backdrop
        createBackdrop();

        // Only lock scroll if it wasn't already locked
        if (!document.body.dataset.scrollY) {
            lockScroll();
        }

        const triggerText = trigger.textContent.trim();
        announceToScreenReader(`${triggerText} menu expanded`);

        setTimeout(() => {
            const firstFocusable = panel.querySelector('[tabindex="0"]');
            firstFocusable?.focus();
        }, 100);
    };

    /**
     * Setup triggers
     */
    triggers.forEach((trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);

        if (!panel) return;

        const closeButton = panel.querySelector(SELECTORS.close);
        const tabs = panel.querySelectorAll(SELECTORS.megaTab);

        if (tabs.length > 0) {
            setActiveTab(panel, 0);
        }

        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            const isOpen = trigger.getAttribute('aria-expanded') === 'true';

            if (isOpen) {
                closePanel();
            } else {
                openPanelMenu(trigger);
            }
        });

        closeButton?.addEventListener('click', (event) => {
            event.preventDefault();
            closePanel();
        });

        tabs.forEach((tab) => {
            tab.addEventListener('click', (event) => {
                event.preventDefault();
                const index = datasetNumber(tab, 'megaTab');
                setActiveTab(panel, index);
            });

            tab.addEventListener('keydown', (event) => {
                handleTabKeyboard(event, panel);
            });
        });

        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && event.shiftKey) {
                const url = trigger.dataset.megaUrl;
                if (url) {
                    window.location.href = url;
                }
            }
        });
    });

    /**
     * Close on outside click
     */
    document.addEventListener('click', (event) => {
        if (!openPanel) return;

        if (!nav.contains(event.target) && !openPanel.contains(event.target)) {
            closePanel();
        }
    });

    /**
     * Close on Escape
     */
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && openPanel) {
            closePanel();
        }
    });

    /**
     * Update panel position on window changes
     */
    const handleWindowChange = () => {
        if (openPanel && openTrigger) {
            setPanelPosition(openTrigger, openPanel);
        }
    };

    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange);

    return () => {
        window.removeEventListener('resize', handleWindowChange);
        window.removeEventListener('scroll', handleWindowChange);
    };
};
